/**
 * Axios Configuration
 *
 * Centralized HTTP client with:
 * - Request interceptor for attaching auth token
 * - Response interceptor for token refresh on 401
 * - Automatic retry of failed requests after token refresh
 */

import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';

import { API_ENDPOINTS } from './endpoints';
import {
  getStoredToken,
  getStoredRefreshToken,
  setStoredToken,
  setStoredRefreshToken,
  clearAuthStorage,
} from '@/utils/storage.utils';

// ============================================
// Configuration
// ============================================

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const API_TIMEOUT = 30000; // 30 seconds

// ============================================
// Create Axios Instance
// ============================================

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================
// Request Interceptor
// ============================================

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getStoredToken();

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// ============================================
// Response Interceptor with Token Refresh
// ============================================

// Track if we're currently refreshing the token
let isRefreshing = false;

// Queue of requests waiting for token refresh
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
  config: InternalAxiosRequestConfig;
}> = [];

/**
 * Process the queue of failed requests after token refresh
 */
const processQueue = (error: Error | null, token: string | null = null): void => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
      resolve(apiClient(config));
    }
  });

  failedQueue = [];
};

/**
 * Handle logout - clear storage and redirect to login
 */
const handleLogout = (): void => {
  clearAuthStorage();
  // Use window.location for a full page reload to ensure clean state
  window.location.href = '/login';
};

/**
 * Attempt to refresh the access token
 */
const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = getStoredRefreshToken();

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  // Use a separate axios instance to avoid interceptor loop
  const response = await axios.post<{
    status: string;
    statusCode: number;
    result: {
      token: string;
      refreshToken: string;
    };
  }>(
    `${API_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`,
    { refreshToken },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (response.data.status === 'success' && response.data.result) {
    const { token, refreshToken: newRefreshToken } = response.data.result;
    setStoredToken(token);
    setStoredRefreshToken(newRefreshToken);
    return token;
  }

  throw new Error('Token refresh failed');
};

// Extended config type to track retry attempts
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

apiClient.interceptors.response.use(
  // Success response - pass through
  (response) => response,

  // Error response - handle 401 and token refresh
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    // If no config, can't retry
    if (!originalRequest) {
      return Promise.reject(error);
    }

    // ============================================
    // 403 — RBAC permission denied
    // ============================================
    // NO toast for 403s: screens routinely fire calls the user's role doesn't
    // cover, and one toast per blocked call is noise. Instead we mark the error
    // (so page catch blocks can skip their own toast via error.isPermissionDenied)
    // and, on RBAC_DENIED, refetch permissions so the UI re-gates itself.
    if (error.response?.status === 403) {
      const data = error.response.data as { result?: { code?: string; message?: string } } | undefined;
      (error as AxiosError & { isPermissionDenied?: boolean }).isPermissionDenied = true;
      if (data?.result?.code === 'RBAC_DENIED') {
        window.dispatchEvent(new CustomEvent('rbac:denied', { detail: data.result }));
        console.warn(
          '🔒 RBAC_DENIED:',
          originalRequest.url,
          '— missing permission:',
          data.result.message ?? ''
        );
      } else {
        console.warn('🔒 403 Forbidden:', originalRequest.url);
      }
    }

    // Check if this is a 401 error (unauthorized)
    const isUnauthorized = error.response?.status === 401;

    // Don't retry if:
    // - Not a 401 error
    // - Already retried this request
    // - This is the refresh token endpoint itself
    // - This is the login endpoint
    if (
      !isUnauthorized ||
      originalRequest._retry ||
      originalRequest.url === API_ENDPOINTS.AUTH.REFRESH ||
      originalRequest.url === API_ENDPOINTS.AUTH.LOGIN
    ) {
      return Promise.reject(error);
    }

    // Mark this request as retried
    originalRequest._retry = true;

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject, config: originalRequest });
      });
    }

    isRefreshing = true;

    try {
      const newToken = await refreshAccessToken();

      // Process queued requests with new token
      processQueue(null, newToken);

      // Retry the original request with new token
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
      }

      return apiClient(originalRequest);
    } catch (refreshError) {
      // Token refresh failed - process queue with error and logout
      processQueue(refreshError as Error, null);
      handleLogout();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// ============================================
// API Helper Functions
// ============================================

/**
 * Standard API response format from backend
 */
export interface ApiResponse<T> {
  status: 'success' | 'error';
  statusCode: number;
  statusMessage?: string;
  result: T;
}

/**
 * Pagination request parameters
 */
export interface PaginationParams {
  page: number;
  size: number;
}

/**
 * Standard list response from backend
 */
export interface ListResponse<T> {
  list: T[];
  totalRecords: number;
  filteredRecords: number;
}

/**
 * Make a GET request
 */
export async function apiGet<T>(url: string): Promise<ApiResponse<T>> {
  const response = await apiClient.get<ApiResponse<T>>(url);
  return response.data;
}

/**
 * Make a POST request
 */
export async function apiPost<T, D = unknown>(url: string, data?: D): Promise<ApiResponse<T>> {
  const response = await apiClient.post<ApiResponse<T>>(url, data);
  return response.data;
}

/**
 * Make a PUT request
 */
export async function apiPut<T, D = unknown>(url: string, data?: D): Promise<ApiResponse<T>> {
  const response = await apiClient.put<ApiResponse<T>>(url, data);
  return response.data;
}

/**
 * Make a DELETE request
 */
export async function apiDelete<T>(url: string): Promise<ApiResponse<T>> {
  const response = await apiClient.delete<ApiResponse<T>>(url);
  return response.data;
}

export default apiClient;
